import { Component, OnInit } from '@angular/core';
import { CreateRepositoryRequest, RepositoryDetail, RepositoryFormData } from 'src/app/core/models/repository';
import { ConfigEntityService } from 'src/app/core/services/config-entity.service';
import { CreateRepositoryComponent } from '../modal/create-repository/create-repository.component';
import { MatDialog } from '@angular/material/dialog';
import { NgxSpinnerService } from 'ngx-spinner';
import { MatSnackBar } from '@angular/material/snack-bar';

@Component({
  selector: 'app-repository-management',
  templateUrl: './repository-management.component.html',
  styleUrls: ['./repository-management.component.scss']
})
export class RepositoryManagementComponent implements OnInit {
  repositories: RepositoryDetail[] = [];
  filteredRepositories: RepositoryDetail[] = [];
  searchTerm = '';
  statusFilter = '';
  showCreateForm = false;
  isLoading = false;
  isCreating = false;
  successMessage = '';
  errorMessage = '';

  constructor(
    private configEntityService: ConfigEntityService,
    public dialog: MatDialog,
    private spinner: NgxSpinnerService,
    private snackbar: MatSnackBar
  ) {}

  ngOnInit() {
    this.loadRepositories();
  }

  loadRepositories() {
    try{
      this.spinner.show();
      this.isLoading = true;
      this.configEntityService.getAllRepositories().subscribe({
        next: (data) => {
          this.spinner.hide();
          this.repositories = data;
          this.filteredRepositories = [...data];
          this.isLoading = false;
        },
        error: (error) => {
          this.spinner.hide();
          this.errorMessage = 'Failed to load repositories';
          this.isLoading = false;
        }
      });
    }
    catch(error) {
      this.spinner.hide();
      this.errorMessage = 'Failed to load repositories';
      console.error('Error in loadRepositories:', error);
    }
  }

  createRepository() {
    try{
      const dialogRef = this.dialog.open(CreateRepositoryComponent, {
          width: "50%",
          autoFocus: true,
          disableClose: true,
          hasBackdrop: false,
          panelClass: "job-dialog-containers",
          data: { name: "", updateForm: false, isBuildAndPush: false },
        });
        dialogRef.afterClosed().subscribe((res) => {
          if (res) {
            this.loadRepositories();
          }
      });
    }
    catch(error) {
      console.error('Error in createRepository:', error);
      this.snackbar.open('Failed to open repository creation dialog', 'Close', {
        duration: 5000,
        panelClass: ['error-snackbar']
      });
      return;
    }
    
  }

  filterRepositories() {
    let filtered = [...this.repositories];

    // Apply text search
    if (this.searchTerm) {
      filtered = filtered.filter(repo =>
        repo.name.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
        repo.registry.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
        repo.tags?.some(tag => tag.toLowerCase().includes(this.searchTerm.toLowerCase()))
      );
    }

    // Apply status filter
    if (this.statusFilter) {
      switch (this.statusFilter) {
        case 'recent':
          filtered = filtered.filter(repo => {
            const daysSinceUpdate = (Date.now() - new Date(repo.last_updated).getTime()) / (1000 * 60 * 60 * 24);
            return daysSinceUpdate <= 7;
          });
          break;
        case 'many-tags':
          filtered = filtered.filter(repo => repo.tag_count >= 3);
          break;
        case 'single-tag':
          filtered = filtered.filter(repo => repo.tag_count === 1);
          break;
      }
    }

    this.filteredRepositories = filtered;
  }


  formatDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }

  getRelativeTime(dateString: string): string {
    const now = new Date();
    const date = new Date(dateString);
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffInSeconds < 60) return 'just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`;
    if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 86400)} days ago`;
    if (diffInSeconds < 31536000) return `${Math.floor(diffInSeconds / 2592000)} months ago`;
    return `${Math.floor(diffInSeconds / 31536000)} years ago`;
  }

  getEmptyMessage(): string {
    if (this.searchTerm && this.statusFilter) {
      return 'No repositories match your search and filter criteria';
    } else if (this.searchTerm) {
      return 'No repositories match your search';
    } else if (this.statusFilter) {
      return 'No repositories match your filter';
    } else {
      return 'No repositories found';
    }
  }

  clearMessages() {
    this.successMessage = '';
    this.errorMessage = '';
  }
}
